import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Banknote, Bot, BadgeDollarSign, CreditCard, Globe, Route, Wallet, Link2 } from 'lucide-react';
import { PaperTradingSettingsPage } from './PaperTradingSettings';
import { EquityLiveTradingSettingsPage } from './EquityLiveTradingSettings';
import { IgTradingSettingsPage } from './IgTradingSettings';
import { BrokerRoutingSettingsPage } from './BrokerRoutingSettings';
import { BrokerConnectionsSettingsPage } from './BrokerConnectionsSettings';
import { PaperAccountsSettingsPage } from './PaperAccountsSettings';
import { BillingSettingsPage } from './BillingSettings';
import { AiSettingsPage } from './AiSettings';

// ── Tile model ───────────────────────────────────────────────────

/**
 * One configuration category. Adding a new tile (e.g. Notifications)
 * is a single CONFIG_TILES entry — no other refactoring required.
 */
export interface ConfigurationTile {
  id: string;
  label: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  /** Detail view component for this tile. */
  component: React.FC;
}

const CONFIG_TILES: ConfigurationTile[] = [
  {
    id: 'billing',
    label: 'Plan & Billing',
    description: 'See your plan, change it, and manage payment.',
    icon: CreditCard,
    component: BillingSettingsPage,
  },
  {
    id: 'paper-trading',
    label: 'Paper Trading',
    description: 'Connect your broker account and enable paper-trading orders.',
    icon: Banknote,
    component: PaperTradingSettingsPage,
  },
  {
    id: 'paper-accounts',
    label: 'Paper Accounts',
    description: 'Separate pots of paper capital so each strategy trades its own balance.',
    icon: Wallet,
    component: PaperAccountsSettingsPage,
  },
  {
    id: 'equity-live-trading',
    label: 'Equity Live Trading',
    description: 'Connect your real-money Alpaca account and arm live equity trading.',
    icon: BadgeDollarSign,
    component: EquityLiveTradingSettingsPage,
  },
  {
    id: 'ig-markets',
    label: 'IG Markets',
    description: 'Connect your IG account to trade US shares and forex as CFDs (demo or live).',
    icon: Globe,
    component: IgTradingSettingsPage,
  },
  {
    id: 'broker-connections',
    label: 'Broker Connections',
    description: 'Connect Tradier, TradeStation, tastytrade, Schwab, crypto exchanges, cTrader or MetaTrader.',
    icon: Link2,
    component: BrokerConnectionsSettingsPage,
  },
  {
    id: 'broker-routing',
    label: 'Broker Routing',
    description: 'Choose which broker handles equities and forex (Alpaca / FXCM / IG).',
    icon: Route,
    component: BrokerRoutingSettingsPage,
  },
  {
    id: 'ai-assistant',
    label: 'AI Assistant',
    description: 'Configure the AI for analysis, guidance, and automated trading.',
    icon: Bot,
    component: AiSettingsPage,
  },
  // Future: { id: 'notifications', ... }, { id: 'display', ... }, etc.
];

// ── Hash helpers — drill-down lives at #/dashboard/configuration/<tile-id> ──
//
// Coexists with query-style hashes (#/dashboard/configuration?...): we slice
// off any "?..." before splitting on "/".

function readActiveTile(): string | null {
  const hash = window.location.hash.replace(/^#\/?/, '');
  const beforeQuery = hash.split('?')[0];
  // beforeQuery looks like "dashboard/configuration" or
  // "dashboard/configuration/paper-trading".
  const parts = beforeQuery.split('/').filter(Boolean);
  if (parts[0] !== 'dashboard' || parts[1] !== 'configuration') return null;
  return parts[2] ?? null;
}

function pushTileHash(tileId: string | null) {
  const hash = window.location.hash;
  const queryPart = hash.includes('?') ? '?' + hash.split('?')[1] : '';
  const newHash = tileId
    ? `#/dashboard/configuration/${tileId}${queryPart}`
    : `#/dashboard/configuration${queryPart}`;
  if (window.location.hash !== newHash) {
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search + newHash,
    );
  }
}

// ── Page ─────────────────────────────────────────────────────────

export const ConfigurationPage: React.FC = () => {
  const [activeTileId, setActiveTileId] = useState<string | null>(() => readActiveTile());

  // Stay in sync with browser nav (back/forward, tab switches that drop us
  // back to #/configuration).
  useEffect(() => {
    const onHash = () => setActiveTileId(readActiveTile());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const openTile = (id: string) => {
    pushTileHash(id);
    setActiveTileId(id);
  };

  const goBack = () => {
    pushTileHash(null);
    setActiveTileId(null);
  };

  const activeTile = activeTileId
    ? CONFIG_TILES.find(t => t.id === activeTileId) ?? null
    : null;

  // ── Detail view ──
  if (activeTile) {
    const Detail = activeTile.component;
    return (
      <div className="p-5 space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={goBack}
          className="-ml-2"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Configuration
        </Button>
        <Detail />
      </div>
    );
  }

  // ── Tile grid ──
  return (
    <div className="p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Configuration</h2>
        <p className="text-xs text-muted-foreground">Settings & integrations</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {CONFIG_TILES.map(tile => {
          const Icon = tile.icon;
          return (
            <Card
              key={tile.id}
              onClick={() => openTile(tile.id)}
              className="p-4 cursor-pointer hover:bg-accent/40 hover:border-primary/40 transition-colors"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openTile(tile.id);
                }
              }}
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{tile.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {tile.description}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ConfigurationPage;
